import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { getDetail } from '@api/home/detail/action';
import { useSearch } from '@hooks';
import { getRelativeUrl } from '@utils';
import { Knife, Type } from '@utils/tool';

import { PageData } from './types';

const { map } = Knife

/**
 *
 * 权重算法匹配sku相关度最高的相邻数据
 * var arr = [28, 34, 43, 19]
  var targets = [
  [3, 28, 17, 19],
  [15, 43, 34, 19],
  [28, 19, 34, 12],
  [43, 17, 19, 28],
  [28, 34, 19, 29],
  [28, 34, 43, 29],
  [28, 24, 43, 19]
  ]
  weightCalculation(arr, targets, 3)

 */
const weightCalculation = (
  arr: string[],
  targets: string[][],
  weightedValue: number
) => {
  const results = {}

  for (let i = 0; i < arr.length; i++) {
    let weight = (arr.length - i) * 10
    for (let j = 0; j < targets.length; j++) {
      const key = targets[j].join('-')

      results[key] || (results[key] = [])

      if (arr[i] === targets[j][i]) {
        //
        if (i === weightedValue) {
          weight = weight * arr.length * 10
        }
        results[key].push(weight)
      } else {
        results[key].push(0)
      }
      //result.push(arr[i] === targets[j][i])
    }
    // results.push(result)
  }

  let weight: { key: string; value: number }[] = []
  Object.keys(results).forEach((key) => {
    weight.push({
      key: key,
      value: results[key].reduce((pre, cur) => pre + cur, 0)
    })
  })

  weight.sort((a, b) => b.value - a.value)

  return weight.map((i) => i.key)
}

const useScroll = (callback: Function) => {
  const location = useLocation()

  React.useEffect(() => {
    //商品推荐进入路由 主动刷新数据
    if (location.state?.from === '/recommend') {
      callback(location.state.id)
      return
    }
    // useSkuId(products_sku_id)
  }, [location.state?.id])

  //路由不变时将窗口拉取到顶部,涉及当前页面跳转
  React.useEffect(() => {
    if (location.state?.from === '/self') {
      return
    }
    window.scrollTo(0, 0)
  }, [location.state?.id])
}

/**
 * 获取商品sku
 * @testdata 171
 */

export const useSku = (
  data?: PageData,
  reqUrl = 'products',
  extraParams = {}
) => {
  const navigate = useNavigate()

  //搜索详情页数据
  const {
    mutation,
    data: pageData = data,
    isLoading,
    isError
  } = useSearch(
    (id, params = {}, extra = extraParams) =>
      getDetail(id, {
        ...params,
        ...extra,
        item_products_id: reqUrl === 'item' ? 1 : 0
      }),
    ['mutation']
  )
  useScroll((id) => mutation(id))

  const skuList = Type.array(pageData?.saleAttrList)

  const skuDefualtOpions = map(pageData?.productInfo?.saleAttrs, (data) => {
    return {
      parentId: data.productsOptionsId,
      child: {
        id: data.productsOptionsValuesId,
        name: data.productsOptionsValuesName
      }
    }
  })

  const mapSkuId = (
    obj: (typeof skuDefualtOpions)[number],
    currentIndex?: number
  ) => {
    const { parentId, child } = obj

    const copyData = Knife.copy(skuDefualtOpions, true)

    Knife.find(copyData, { parentId }, {}).child = child

    const ids = copyData.map((i) => i.child.id)

    //通过sku项目id拼接 skuKey
    const mapSkuKey = ids.join('-')

    // console.log('mapSkuKey', mapSkuKey)
    //通过key找到skuId
    const getSkuId = pageData?.saleAttrValueMap[mapSkuKey]

    if (Type.isNever(getSkuId) && !Type.isNever(currentIndex)) {
      const availableSkuIds = Knife.map(
        pageData?.saleAttrValueMap,
        (_, key: string) => key.split('-')
      )

      const relatedSkuKey = weightCalculation(
        ids,
        availableSkuIds,
        currentIndex
      )[0]
      //console.log('relatedSkuKey', relatedSkuKey)

      return pageData?.saleAttrValueMap[relatedSkuKey]
    }

    return getSkuId
  }

  const findImgSrcFromSkuId = (
    obj: (typeof skuDefualtOpions)[number],
    index
  ) => {
    const id = mapSkuId(obj, index)
    const data = Knife.find<any, any>(pageData?.skuList, { productsSkuId: id })
    // console.log('imageSku', id, data)
    return data?.imagePath
  }

  const findGoodsInfoFromSkuId = (
    obj: (typeof skuDefualtOpions)[number],
    index
  ) => {
    const id = mapSkuId(obj, index)
    const data = Knife.find<any, any>(pageData?.skuList, { productsSkuId: id })
    return data
  }

  //通知sku-id改变, 拼接新sku组合, 发送请求
  const onSkuChange = (
    obj: (typeof skuDefualtOpions)[number],
    currentIndex: number
  ) => {
    const result = mapSkuId(obj, currentIndex)
    if (Type.isNever(result)) return

    const data = Knife.find<any, any>(pageData?.skuList, {
      productsSkuId: result
    })

    mutation(result).then(() => {
      if (reqUrl === 'quickView') return
      navigate(getRelativeUrl(data.jumpUrl).replace('products', reqUrl), {
        replace: true,
        state: { from: '/self', id: result }
      })
    })
  }

  const matchAvialidSkuId = (id: string) => {
    return Type.isNever(pageData?.saleAttrValueMap[id])
  }

  const [meta] = skuList

  const isOnlyOne = skuList.length === 0

  return {
    //  skuId,
    mutation,
    isLoading,
    isError,
    pageData,
    isOnlyOne,
    skuList,
    skuDefualtOpions,
    onSkuChange,
    findImgSrcFromSkuId,
    matchAvialidSkuId,
    findGoodsInfoFromSkuId
  }
}
